import { buttonStyle } from 'components/buttons/buttonStyle';
import { SearchIcon } from 'components/icons';
import SearchBar from 'components/tables/list-table/filter-pieces/SearchBar';
import { Button } from 'components/ui/button';
import useAuth from 'hooks/useAuth';
import PaddedLayout from 'layout/MainLayout/PaddedLayout.tsx';
import { ReactNode, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    useGetBackOfficeSettingsQuery,
    useGetEmployeePickersQuery,
    useGetRequiredFieldsQuery,
} from 'store/api/back-office-settings';
import { useGetTenantConfigurationQuery } from 'store/api/tenants.ts';
import { FeatureFlag } from 'types/features.ts';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { INVOICE_AUTO_GENERATION_SCHEDULE_SECTION_ID } from 'utils/invoiceAutoGenerationSchedule';
import { useHasPermissions } from 'utils/permissionUtil';
import { useClassifiers } from 'utils/useClassifiers.ts';
import { CaseClosingConfig } from './caseClosing/CaseClosingConfig.tsx';
import { ClientAssetAccountsControl } from './ClientAssetAccountsControl.tsx';
import { CompanyLogoOnInvoiceControl } from './CompanyLogoOnInvoiceControl.tsx';
import { CONFIGURATION_CARD_CLASS } from './configurationCard';
import { ConfigurationNav } from './ConfigurationNav';
import CurrencyPrecision from './CurrencyPrecision.tsx';
import DefaultHandledByEmployeesControl from './DefaultHandledByEmployeesControl.tsx';
import DebtCollection from './DebtCollection.tsx';
import DefaultAdminFeePercentageControl from './DefaultAdminFeePercentageControl.tsx';
import DraftInvoiceSupervisingPartnerNotificationControl from './DraftInvoiceSupervisingPartnerNotificationControl.tsx';
import DraftRetentionDaysControl from './DraftRetentionDaysControl.tsx';
import RankingsRefereeRestPeriodControl from './RankingsRefereeRestPeriodControl';
import HideInvoicesOlderThanDaysControl from './HideInvoicesOlderThanDaysControl.tsx';
import ImportationDateControl from './ImportationDateControl.tsx';
import InvoiceAutoGenerationScheduleControl from './InvoiceAutoGenerationScheduleControl.tsx';
import { InsiderListConfig } from './insiderList/InsiderListConfig.tsx';
import PrimaryOfficeForCasesControl from './PrimaryOfficeForCasesControl.tsx';
import { CARD_KEYS, rowLabel } from './settings/requiredFieldLayout';
import { SETTING_SECTIONS } from './settings/settingSections';
import { sectionAnchor, SettingsSection } from './settings/SettingsSection';
import { filterSections, shownSections } from './settings/settingVisibility';
import type { RequiredFieldForm } from 'types/back-office-settings';
import { TenantIconControl } from './TenantIconControl.tsx';
import TimecardEntryControl from './TimecardEntryControl.tsx';
import TimecardIncrementControl from './TimecardIncrementControl.tsx';
import { TimecardTemplatesControl } from './TimecardTemplatesControl.tsx';
import { useAdministratorField } from 'hooks/useAdministratorField';

interface ConfigurationBlock {
    /** The anchor the "On this page" entry scrolls to. */
    id: string;
    titleId: string;
    flag?: FeatureFlag;
    content: ReactNode;
    /** The block carries the anchor itself. */
    ownsAnchor?: boolean;
}

const settingText = (key: string) =>
    `${t(`back-office-settings.${key}.label`)}\n${t(`back-office-settings.${key}.description`)}`;

const requiredFieldText = (administratorFieldName: string) => (form: RequiredFieldForm, field?: string) =>
    field
        ? rowLabel(form, field, administratorFieldName)
        : t(`back-office-settings.required-fields.cards.${CARD_KEYS[form]}.title`);

/**
 * The firm's configuration: the setting sections under a filter, then the configuration blocks. The page waits for the
 * settings as well as the configuration, so nothing above a block moves it after a link scrolled to its anchor.
 */
const ConfigurationPage = () => {
    const intl = useIntl();
    const { classifiers } = useClassifiers({ codes: ['DEBT_REMINDER_TYPE'] });
    const { data: tenantConfiguration } = useGetTenantConfigurationQuery();
    const { data: settings = [], isLoading: settingsLoading } = useGetBackOfficeSettingsQuery();
    const { data: requiredFields = [], isLoading: requiredFieldsLoading } = useGetRequiredFieldsQuery();
    const { data: employeePickers = [], isLoading: employeePickersLoading } = useGetEmployeePickersQuery();
    const enabledFeatures = useAuth().user?.enabledFeatures ?? [];
    const readOnly = !useHasPermissions(['UPDATE.BACK_OFFICE_SETTINGS']);
    const [filter, setFilter] = useState('');
    const administratorFieldName = useAdministratorField().name;

    if (!tenantConfiguration || settingsLoading || requiredFieldsLoading || employeePickersLoading) {
        return <PaddedLayout>{null}</PaddedLayout>;
    }

    const shown = shownSections(SETTING_SECTIONS, settings, enabledFeatures, requiredFields, employeePickers);
    const filtered = filterSections(shown, filter, settingText, requiredFieldText(administratorFieldName));
    const filtering = filter.trim() !== '';
    const noMatch = filtering && filtered.length === 0;
    const total = shown.reduce((sum, section) => sum + section.total, 0);
    const on = shown.reduce((sum, section) => sum + section.on, 0);

    const blocks: ConfigurationBlock[] = [
        {
            id: 'configuration-currency-precision',
            titleId: 'configuration.currency-precision',
            content: <CurrencyPrecision tenantConfiguration={tenantConfiguration} />,
        },
        {
            id: 'configuration-draft-retention',
            titleId: 'configuration.draft-retention-days-control',
            flag: FeatureFlag.SAVE_AS_DRAFT,
            content: <DraftRetentionDaysControl tenantConfiguration={tenantConfiguration} />,
        },
        {
            id: 'configuration-debt-collection',
            titleId: 'configuration.debt-collection',
            content: <DebtCollection classifiers={classifiers} tenantConfiguration={tenantConfiguration} />,
        },
        {
            id: 'configuration-timecard-entry-control',
            titleId: 'configuration.timecard-entry-control',
            content: <TimecardEntryControl tenantConfiguration={tenantConfiguration} />,
        },
        {
            id: 'configuration-timecard-increments',
            titleId: 'configuration.timecard-increment-control',
            flag: FeatureFlag.TIMECARD_INCREMENT_CONTROL,
            content: <TimecardIncrementControl tenantConfiguration={tenantConfiguration} />,
        },
        {
            id: 'configuration-timecard-templates',
            titleId: 'configuration.timecard-templates',
            flag: FeatureFlag.TIMECARD_TEMPLATES,
            content: <TimecardTemplatesControl />,
        },
        {
            id: 'configuration-importation-date',
            titleId: 'configuration.importation-date-control',
            flag: FeatureFlag.IMPORTATION_DATE_CONFIGURATION,
            content: <ImportationDateControl tenantConfiguration={tenantConfiguration} />,
        },
        {
            id: 'configuration-primary-office',
            titleId: 'configuration.primary-office-for-cases',
            flag: FeatureFlag.HANDLED_BY_OFFICE_CONTROL,
            content: <PrimaryOfficeForCasesControl tenantConfiguration={tenantConfiguration} />,
        },
        {
            id: 'configuration-default-handled-by',
            titleId: 'configuration.default-handled-by',
            content: <DefaultHandledByEmployeesControl tenantConfiguration={tenantConfiguration} />,
        },
        {
            id: 'configuration-invoice-visibility',
            titleId: 'configuration.hide-invoices-older-than-days-control',
            content: <HideInvoicesOlderThanDaysControl tenantConfiguration={tenantConfiguration} />,
        },
        {
            id: 'configuration-draft-invoice-notification',
            titleId: 'configuration.draft-invoice-notification-control',
            content: <DraftInvoiceSupervisingPartnerNotificationControl tenantConfiguration={tenantConfiguration} />,
        },
        {
            id: 'configuration-default-admin-fee',
            titleId: 'configuration.default-admin-fee-control',
            flag: FeatureFlag.ADMIN_FEE_MODAL_INVOICES,
            content: <DefaultAdminFeePercentageControl tenantConfiguration={tenantConfiguration} />,
        },
        {
            id: 'configuration-company-logo',
            titleId: 'configuration.invoice-logo-control',
            content: <CompanyLogoOnInvoiceControl tenantConfiguration={tenantConfiguration} />,
        },
        {
            id: 'configuration-tenant-icon',
            titleId: 'configuration.tenant-icon-control',
            content: <TenantIconControl tenantConfiguration={tenantConfiguration} />,
        },
        {
            id: INVOICE_AUTO_GENERATION_SCHEDULE_SECTION_ID,
            titleId: 'configuration.invoice-auto-generation-schedule',
            content: <InvoiceAutoGenerationScheduleControl tenantConfiguration={tenantConfiguration} />,
            ownsAnchor: true,
        },
        {
            id: 'configuration-case-closing',
            titleId: 'case-closing-config.case-closing',
            content: <CaseClosingConfig caseClosing={tenantConfiguration.caseClosing} />,
        },
        {
            id: 'configuration-client-asset-accounts',
            titleId: 'configuration.client-asset-accounts',
            flag: FeatureFlag.PARTY_ASSETS,
            content: <ClientAssetAccountsControl tenantConfiguration={tenantConfiguration} />,
        },
        {
            id: 'configuration-insider-list',
            titleId: 'configuration.insider-list.title',
            flag: FeatureFlag.MATTER_INSIDER_LIST,
            content: <InsiderListConfig />,
        },
        {
            id: 'configuration-rankings-referee',
            titleId: 'configuration.rankings-referee.title',
            flag: FeatureFlag.RANKINGS_REFEREE,
            content: <RankingsRefereeRestPeriodControl tenantConfiguration={tenantConfiguration} />,
        },
    ].filter((block: ConfigurationBlock) => !block.flag || enabledFeatures.includes(block.flag));

    return (
        <PaddedLayout>
            <div className="flex h-full flex-wrap items-start gap-8">
                <div className="flex min-w-0 flex-[999_1_560px] flex-col gap-4">
                    <div className="new:gap-5 flex flex-col gap-4">
                        <div className="new:flex-row new:flex-wrap new:items-center new:gap-3 flex flex-col gap-2">
                            <SearchBar
                                type="search"
                                value={filter}
                                onChange={(event) => setFilter(event.target.value)}
                                placeHolder={t('back-office-settings.filter-placeholder')}
                                aria-label={t('back-office-settings.filter-label')}
                                className="new:h-9 new:w-80 new:max-w-full new:border-border-control new:shadow-none"
                            />
                            <p className="text-body1 text-grey-600 new:text-meta new:text-muted-foreground m-0">
                                <FormattedMessage
                                    id="back-office-settings.count"
                                    values={{
                                        settings: total,
                                        on,
                                        b: (chunks) => (
                                            <strong className="text-fg new:font-normal new:text-inherit font-semibold">
                                                {chunks}
                                            </strong>
                                        ),
                                    }}
                                />
                            </p>
                        </div>
                        <p role="status" className="sr-only">
                            {noMatch ? t('back-office-settings.no-match', { query: filter.trim() }) : ''}
                        </p>
                        {noMatch ? (
                            <div
                                className={cn(
                                    CONFIGURATION_CARD_CLASS,
                                    'new:rounded-[10px] flex flex-col items-center gap-2 px-6 py-10 text-center'
                                )}
                            >
                                <SearchIcon className="text-grey-500 size-7" />
                                <p className="text-fg m-0 text-[15px] font-semibold">
                                    <FormattedMessage
                                        id="back-office-settings.no-match"
                                        values={{ query: filter.trim() }}
                                    />
                                </p>
                                <p className="text-grey-600 m-0 text-[13px]">
                                    <FormattedMessage id="back-office-settings.no-match-body" />
                                </p>
                                <Button
                                    {...buttonStyle({ variant: 'outlined' }, 'mt-1 h-8 px-3')}
                                    onClick={() => setFilter('')}
                                >
                                    <FormattedMessage id="back-office-settings.clear-filter" />
                                </Button>
                            </div>
                        ) : (
                            filtered.map((section) => (
                                <SettingsSection key={section.layout.anchor} section={section} readOnly={readOnly} />
                            ))
                        )}
                    </div>
                    {/* Hidden rather than unmounted while filtering, so an edit left in a block survives it. */}
                    <div hidden={filtering} className="flex flex-col gap-4">
                        {blocks.map((block) => (
                            <div
                                key={block.id}
                                id={block.ownsAnchor ? undefined : block.id}
                                className="flex scroll-mt-20 flex-col gap-4"
                            >
                                {block.content}
                            </div>
                        ))}
                    </div>
                </div>
                <ConfigurationNav
                    sections={filtered.map((section) => ({
                        id: sectionAnchor(section.layout.anchor),
                        label: t(`back-office-settings.sections.${section.layout.anchor}`),
                        onLabel:
                            section.total > 0
                                ? intl.formatMessage({ id: 'back-office-settings.on-count' }, { on: section.on })
                                : undefined,
                    }))}
                    blocks={filtering ? [] : blocks.map((block) => ({ id: block.id, label: t(block.titleId) }))}
                />
            </div>
        </PaddedLayout>
    );
};

export default ConfigurationPage;
